import { useEffect, useState } from 'react'
import type { MissionFaction } from '../api/types'
import type { LatLng } from '../import/geo'
import { TOOLS, isComplete, summary, type DrawTool } from '../import/draw'
import { Modal } from './Modal'

/**
 * Drawing on the TOC map, the same six tools as the app.
 *
 * Click to add points, the way the app takes taps. A bar at the bottom carries the running
 * measurement and the three things a half-drawn shape needs -- take a point back, throw it away,
 * keep it -- and Escape and Backspace do the first two, because this is a keyboard as well as a
 * mouse. Measuring is scratch work: it shows the answer and saves nothing.
 */
export function DrawToolbar({
  active,
  onPick,
}: {
  active: DrawTool | null
  onPick: (tool: DrawTool | null) => void
}) {
  return (
    <div className="draw-toolbar" role="toolbar" aria-label="Draw">
      {TOOLS.map((entry) => (
        <button
          key={entry.tool}
          type="button"
          className={active === entry.tool ? 'selected' : ''}
          aria-pressed={active === entry.tool}
          title={entry.hint}
          onClick={() => onPick(active === entry.tool ? null : entry.tool)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d={entry.icon} />
          </svg>
          <span>{entry.label}</span>
        </button>
      ))}
    </div>
  )
}

export function DraftBar({
  tool,
  points,
  onUndo,
  onDiscard,
  onSave,
}: {
  tool: DrawTool
  points: LatLng[]
  onUndo: () => void
  onDiscard: () => void
  onSave: () => void
}) {
  const name = TOOLS.find((entry) => entry.tool === tool)!.label
  const complete = isComplete(tool, points)

  // Escape abandons, Backspace takes a point back, Enter keeps -- unless somebody is typing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      if (event.key === 'Escape') onDiscard()
      else if (event.key === 'Backspace' && points.length > 0) {
        event.preventDefault()
        onUndo()
      } else if (event.key === 'Enter' && complete) onSave()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDiscard, onUndo, onSave, points.length, complete])

  return (
    <div className="draft-bar" role="status">
      <div className="draft-summary">
        <strong>{name}</strong>
        <span className="mono">{summary(tool, points)}</span>
        <span className="tertiary">
          {points.length} point{points.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="secondary" onClick={onUndo} disabled={points.length === 0} title="Backspace">
          Undo
        </button>
        <button className="secondary" onClick={onDiscard} title="Escape">
          {tool === 'measure' ? 'Done' : 'Discard'}
        </button>
        {tool !== 'measure' && (
          <button onClick={onSave} disabled={!complete} title="Enter">
            Save
          </button>
        )}
      </div>
    </div>
  )
}

const KINDS = [
  { value: 'FRIEND', label: 'Friendly' },
  { value: 'HOSTILE', label: 'Hostile' },
  { value: 'NEUTRAL', label: 'Neutral' },
  { value: 'UNKNOWN', label: 'Unknown' },
] as const

const COLOURS = [
  { hex: null, name: 'By kind' },
  { hex: '#5B8FE0', name: 'Blue' },
  { hex: '#E24A42', name: 'Red' },
  { hex: '#4FA85E', name: 'Green' },
  { hex: '#E5C94B', name: 'Yellow' },
  { hex: '#E8913A', name: 'Orange' },
  { hex: '#B07CE0', name: 'Purple' },
  { hex: '#E8EFE9', name: 'White' },
]

export interface DrawDetails {
  label: string
  remarks: string
  /** 'MISSION' for everyone, or a faction key for one side. */
  audience: string
  affiliation: (typeof KINDS)[number]['value']
  colorHex: string | null
  objective: boolean
}

/** What a finished drawing is called and who sees it, asked once, before it is sent. */
export function DrawSaveModal({
  tool,
  factions,
  objective,
  onCancel,
  onSave,
}: {
  tool: DrawTool
  factions: MissionFaction[]
  /** Started from "Place objective": an objective, shown to everyone, unless changed. */
  objective: boolean
  onCancel: () => void
  onSave: (details: DrawDetails) => Promise<void>
}) {
  const name = TOOLS.find((entry) => entry.tool === tool)!.label
  const [details, setDetails] = useState<DrawDetails>({
    label: '',
    remarks: '',
    audience: 'MISSION',
    affiliation: objective ? 'NEUTRAL' : 'FRIEND',
    colorHex: null,
    objective,
  })
  const [busy, setBusy] = useState(false)
  const set = (patch: Partial<DrawDetails>) => setDetails((current) => ({ ...current, ...patch }))

  return (
    <Modal title={objective ? 'New objective' : `New ${name.toLowerCase()}`} onClose={onCancel}>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault()
          setBusy(true)
          await onSave(details)
          setBusy(false)
        }}
      >
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="draw-label">Name</label>
          <input
            id="draw-label"
            autoFocus
            maxLength={64}
            value={details.label}
            placeholder={objective ? 'Objective Alpha' : name}
            onChange={(event) => set({ label: event.target.value })}
          />
        </div>

        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="draw-remarks">Briefing (optional)</label>
          <textarea
            id="draw-remarks"
            maxLength={2000}
            value={details.remarks}
            onChange={(event) => set({ remarks: event.target.value })}
          />
        </div>

        <fieldset className="plain-fieldset">
          <legend>Who sees it</legend>
          <div className="segmented" role="radiogroup">
            <button
              type="button"
              role="radio"
              aria-checked={details.audience === 'MISSION'}
              className={details.audience === 'MISSION' ? 'selected' : ''}
              onClick={() => set({ audience: 'MISSION' })}
            >
              Everyone
            </button>
            {factions.map((faction) => (
              <button
                key={faction.key}
                type="button"
                role="radio"
                aria-checked={details.audience === faction.key}
                className={details.audience === faction.key ? 'selected' : ''}
                onClick={() => set({ audience: faction.key })}
              >
                {faction.name} only
              </button>
            ))}
          </div>
        </fieldset>

        {tool === 'marker' && (
          <fieldset className="plain-fieldset">
            <legend>What it marks</legend>
            <div className="segmented" role="radiogroup">
              {KINDS.map((kind) => (
                <button
                  key={kind.value}
                  type="button"
                  role="radio"
                  aria-checked={details.affiliation === kind.value}
                  className={details.affiliation === kind.value ? 'selected' : ''}
                  onClick={() => set({ affiliation: kind.value })}
                >
                  {kind.label}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <fieldset className="plain-fieldset">
          <legend>Colour</legend>
          <div className="swatches">
            {COLOURS.map((colour) => (
              <button
                key={colour.name}
                type="button"
                aria-label={colour.name}
                aria-pressed={details.colorHex === colour.hex}
                title={colour.name}
                className={`swatch${details.colorHex === colour.hex ? ' selected' : ''}`}
                style={{ background: colour.hex ?? 'transparent' }}
                onClick={() => set({ colorHex: colour.hex })}
              >
                {colour.hex === null && <span aria-hidden>A</span>}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="check">
          <input
            type="checkbox"
            checked={details.objective}
            onChange={(event) => set({ objective: event.target.checked })}
          />
          List it as an objective
        </label>

        {details.objective && details.audience !== 'MISSION' && (
          <p className="banner warning" style={{ margin: 0 }}>
            An objective only one side can see is an ambush. Fine if that is the scenario.
          </p>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
